import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import userInfoByAttribute from '@nykaa/nes-auth-shared/store/actions/userInfo';
import { fetchSegmentOverNetwork } from '@nykaa/nes-auth-shared/store/helpers';
import {
  IS_REPEAT_USER,
  REPEAT_USER_EXPIRY_DAYS,
  USER_SEGMENT_COOKIE_ERROR,
  ATTRIBUTE_USER_SEGMENT,
  REPEAT_USER_ONE,
  REPEAT_USER_ZERO,
} from '@nykaa/nes-auth-shared/store/constants';
import appEvents from 'store/app/actionTypes';
import PageTypes from 'constants/pageTypes';
import {
  trackUser,
  getCartCountFromStorage,
  setLoginLocation,
} from 'store/auth/helper';
import setUpSessionCount, {
  setSiteVisitSessionCount,
  setUpPdpSessionCount,
} from 'helpers/utils/sessionCount';
import { addAutoSignInEvent } from 'shared/store/smartLock/autoSignin';
import { getEligibleForVariant } from 'shared/helpers/experimentV2';
import { storeCredentials } from 'store/smartLock/helper';
import { RequestConfig } from 'api';
import { handleStoreId } from 'helpers/store';
import { handleDevice } from 'helpers/deviceIdentity';
import { ConfigFlags } from 'store/app/types';
import { setCookie } from '@nykaa/nes-utils/cookies';
import { logger } from '@nykaa/nes-logger';
import { pushData } from '@nykaa/nes-data-layer/utils';
import {
  getAuthSteps,
  deleteAuthSteps,
  isAutoFillShown,
  setAutoFillShown,
  getMobileNumberLocal,
  getRegistration,
  deleteRegistration,
} from 'shared/helpers/auth';
import { getPageType } from 'shared/components/Header/helper';
import { loadVerloop } from 'shared/store/auth/helper';
import {
  AUTH_URL,
  GOOGLE_ONE_EXPERIEMNT,
  LOGIN_LOCATION,
  MOBILE_VERIFICATION_URL,
  PRIVE_TIER_KEY,
} from 'shared/constants/auth';
import { pushMobileVerificationEvents } from 'shared/User/helpers/mobileVerification';
import trackingEvent, {
  LOGIN_CODES,
} from 'shared/User/constants/mobileVerification/trackingEvents';
import checkValidHost from '@nykaa/nes-server/utils';
import WishList from './Wishlist';
import { State } from 'store/types';
import { deleteCookie, getCookie } from 'helpers/utils/cookie';
import {
  ATTRIBUTE_INSTALL_OFFER,
  EXP_AB_AUTOFILL,
  IOC_EXPIRATION_TIME,
  IOC_VALUE,
} from 'shared/constants';
import { styled } from '@nykaa/nes-ui-components';
import { AB_IOC_CART_NUDGE } from 'constants/experiments';
import { localStorageService } from 'service/storage';
import { setIocValue } from 'store/auth/action';
import { GeoSubdivision } from 'store/geoSubdivision/types';
import { User } from 'store/auth/types';

interface BootProps {
  user: User;
  configFlags: ConfigFlags;
  configFlagsFetched: boolean;
  fetchConfigFlags: () => Promise<void>;
  geoSubdivision: GeoSubdivision[];
  geoSubdivisionFetched: boolean;
  fetchGeoSubdivision: () => Promise<void>;
  fetchAuth: () => Promise<void>;
  fetchPriveTier: () => Promise<void>;
  fetchUserHash: () => Promise<void>;
  email: string;
  firstName: string;
  formKey: string;
  id: string;
  isPro: boolean;
  groupId: string;
  dataFetched: boolean;
  fetchedOverNetwork: boolean;
  userName: string;
  storeId: string;
  isStoreEnabled: boolean;
  trackDeviceId: boolean;
  suggestMobile: () => Promise<any>;
  isSuggestedMobileMap: boolean;
  vrnRegionEligibility?: string;
  vrnExpState?: string;
  domains: [];
  isAutoSignUp: boolean;
  showSignUp: () => Promise<void>;
  showChatNow?: boolean;
  isPDPBannerShown: boolean;
  productId: string;
  bucketId: string;
  isPricesRevealedActive: boolean;
  userSegmentConfig: { [key: string]: string };
  isUserSegmentEnabled: boolean;
  showProfile: () => Promise<void>;
  isDifferentialPricingEnabled: boolean;
  priveBand: string;
}

const Iframe = styled.iframe`
  height: 100vh;
  width: 100%;
  border: none;
  border-radius: ${({ theme }) => theme.borders.radius40};
  height: 50%;
  position: fixed;
  bottom: 0;
  border-bottom-left-radius: ${({ theme }) => theme.borders.radiusNone};
  border-bottom-right-radius: ${({ theme }) => theme.borders.radiusNone};
`;

const FrameWrapper = styled.div`
  position: fixed;
  width: 100%;
  background: ${({ theme }) => theme.colors.textDisabled};
  z-index: 4;
  height: 100%;
  border: none;
  border-radius: ${({ theme }) => theme.borders.radius40};
`;

// TODO: most of the user properties can be replaced by user object
// FIXME: use user object instead of user properties individually
const Boot = ({
  user,
  configFlags,
  configFlagsFetched,
  fetchAuth,
  email,
  fetchConfigFlags,
  fetchGeoSubdivision,
  firstName,
  formKey,
  id,
  isPro,
  groupId,
  dataFetched,
  userName,
  fetchedOverNetwork,
  storeId,
  isStoreEnabled,
  trackDeviceId,
  suggestMobile,
  isSuggestedMobileMap,
  vrnRegionEligibility,
  vrnExpState,
  domains,
  isAutoSignUp,
  showSignUp,
  fetchUserHash,
  isPDPBannerShown,
  productId,
  showChatNow,
  bucketId,
  isPricesRevealedActive,
  userSegmentConfig,
  isUserSegmentEnabled,
  isDifferentialPricingEnabled,
  showProfile,
  fetchPriveTier,
  priveBand,
}: BootProps) => {
  const { pathname } = useLocation();
  const isExperimentEnabled = useSelector(
    (state: State) => state?.langConfig?.isExperimentEnabled
  );
  const isZendeskEnabled = useSelector(
    (state: State) => state.remoteConfig.switches.zendeskChatEnabled || false
  );
  const isVerloopEnabled = useSelector(
    (state: State) => state.remoteConfig.switches.verloopChatEnabled || false
  );
  const pageType = getPageType(pathname);
  const [showIframe, setShowIframe] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('');
  const { repeatUser = '' } = userSegmentConfig || {};
  const dispatch = useDispatch();

  useEffect(() => {
    if (isStoreEnabled) {
      handleStoreId(storeId);
    }
    if (isExperimentEnabled) {
      const trackingData = { vrnRegionEligibility, vrnExpState };
      if (!vrnExpState) {
        trackingData.vrnRegionEligibility = 'Error';
      }
      pushData({ ...trackingData });
    }
  }, [
    storeId,
    isStoreEnabled,
    isExperimentEnabled,
    vrnRegionEligibility,
    vrnExpState,
  ]);

  useEffect(() => {
    setUpSessionCount();
  }, []);

  useEffect(() => {
    setSiteVisitSessionCount();
  }, []);

  const fetchUserSegmentInfo = useCallback(() => {
    const isRepeatUser = getCookie(IS_REPEAT_USER);
    const isNewUser = !(isRepeatUser && Number(isRepeatUser));
    if (isNewUser) {
      const fetchSegmentInfo = fetchSegmentOverNetwork();
      if (fetchSegmentInfo) {
        userInfoByAttribute(ATTRIBUTE_USER_SEGMENT)
          .then((response) => {
            if (response) {
              const segmentIds = response.usersegment?.segment || [];
              const isRepeated = segmentIds.some(
                (segmentId: string) => segmentId === repeatUser
              );
              const repeatUserValue = isRepeated
                ? REPEAT_USER_ONE
                : REPEAT_USER_ZERO;
              setCookie(
                IS_REPEAT_USER,
                repeatUserValue,
                REPEAT_USER_EXPIRY_DAYS
              );
            }
          })
          .catch((err) => {
            if (err) {
              logger.error(USER_SEGMENT_COOKIE_ERROR);
            }
          });
      }
    }
  }, [repeatUser]);

  useEffect(() => {
    if (isUserSegmentEnabled && dataFetched && id) {
      fetchUserSegmentInfo();
    }
  }, [dataFetched, fetchUserSegmentInfo, id, isUserSegmentEnabled]);

  useEffect(() => {
    setUpPdpSessionCount(isPDPBannerShown);
  }, [isPDPBannerShown, productId]);

  useEffect(() => {
    window.requestIdleCallback(() => {
      fetchAuth();
      fetchConfigFlags();
      fetchGeoSubdivision();
    });
  }, [fetchAuth, fetchConfigFlags, fetchGeoSubdivision]);

  const isIocExperimentEnabled = useSelector((state: State) =>
    getEligibleForVariant(state, AB_IOC_CART_NUDGE, 'A')
  );

  const { iocValueExpirationTime, iocCookieExpiryTime } = useSelector(
    (state: State) => state.remoteConfig.configs?.cartNudgeConfig
  );

  /**
   * Fetches IOC (Install Offer Count) information if conditions are met.
   * If the current IOC value is not 3 and the IOC has expired, it fetches the
   * latest information from the API and updates the local storage and cookies
   * with the new value and expiration time.
   * @returns {void}
   */
  const fetchIocInfo = (): void => {
    const iocExpiryTime = localStorageService.getItem(IOC_EXPIRATION_TIME);
    const currentDate = new Date().getTime();
    const currentIocValue = getCookie(IOC_VALUE);
    const isIocExpired =
      !iocExpiryTime || currentDate > +iocExpiryTime || !currentIocValue;
    if (currentIocValue != 3 && isIocExpired) {
      userInfoByAttribute(ATTRIBUTE_INSTALL_OFFER).then((response) => {
        const iocValue = response?.install_offer?.count;
        dispatch(setIocValue(iocValue));
        setCookie(IOC_VALUE, iocValue, iocCookieExpiryTime);
        const timeLimit =
          new Date().getTime() + iocValueExpirationTime * 3600000;
        localStorageService.setItem(IOC_EXPIRATION_TIME, timeLimit.toString());
      });
    }
  };

  // Calls user info api to fetch IOC value
  useEffect(() => {
    // checks if experiment is enabled and user is logged in
    if (isIocExperimentEnabled && id) {
      fetchIocInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (dataFetched && formKey) {
      RequestConfig.setFormKey(formKey);
    }
  }, [dataFetched, formKey]);

  useEffect(() => {
    if (dataFetched && isDifferentialPricingEnabled && id) {
      const isLoggedIn = !!email;
      if (isLoggedIn) {
        setCookie(PRIVE_TIER_KEY, priveBand, 1);
      } else if (!isLoggedIn) {
        deleteCookie(PRIVE_TIER_KEY);
      }
    }
  }, [
    dataFetched,
    email,
    fetchPriveTier,
    id,
    isDifferentialPricingEnabled,
    priveBand,
  ]);

  useEffect(() => {
    const userData = {
      email,
      firstName,
      id,
      isPro,
      groupId,
      priveBand,
      dob: user.dob || '',
      gender: user.gender || '',
    };
    if (dataFetched) {
      trackUser(userData);
      if (showChatNow) {
        if (!isZendeskEnabled) {
          const isLoggedIn = !!email;
          if (isLoggedIn) {
            fetchUserHash();
          } else {
            if (isVerloopEnabled) {
              loadVerloop({});
            }
          }
        }
      }

      if (trackDeviceId) {
        handleDevice();
      }
    }
  }, [
    email,
    firstName,
    id,
    dataFetched,
    userName,
    fetchedOverNetwork,
    trackDeviceId,
    groupId,
    isPro,
    fetchUserHash,
    showChatNow,
    isZendeskEnabled,
    priveBand,
  ]);

  useEffect(() => {
    if (dataFetched) {
      const isLoggedIn = !!email;
      if (
        isLoggedIn &&
        getRegistration() &&
        window.location?.pathname?.indexOf(AUTH_URL) < 0
      ) {
        showProfile();
        deleteRegistration();
      }
    }
  }, [dataFetched, email, pathname, showProfile]);

  const handleAutoSignIn = useCallback(() => {
    pushData({ phfill: getMobileNumberLocal() ? 1 : 0 });
    const autoFillVar = getCookie(EXP_AB_AUTOFILL);

    if (configFlags.isSmartLockActive) {
      if (!email) {
        addAutoSignInEvent();
      } else if (fetchedOverNetwork) {
        storeCredentials({ email, userName });
      }
    }
    if (
      autoFillVar === 'A' &&
      !isAutoFillShown() &&
      !email &&
      pageType === PageTypes.HOME
    ) {
      setAutoFillShown();
      setLoginLocation(LOGIN_LOCATION.MYACCOUNT_LOGIN_BTN);
      showSignUp();
      return;
    }
    // else if ((autoFillVar === 'B') &&
    //   (!isAutoFillShown()) && (!email) &&
    //   (pageType !== PageTypes.PRODUCT) &&
    //   (pageType !== PageTypes.SEARCH_LISTING) &&
    //   (pageType !== PageTypes.LISTING)
    // ) {
    //     setAutoFillShown();
    //     showSignUp();
    //     return;
    // }
    else if (isAutoSignUp && !isAutoFillShown() && !email) {
      setAutoFillShown();
      setLoginLocation(LOGIN_LOCATION.MYACCOUNT_LOGIN_BTN);
      showSignUp();
      return;
    }
  }, [
    configFlags.isSmartLockActive,
    email,
    pageType,
    isAutoSignUp,
    fetchedOverNetwork,
    userName,
    showSignUp,
  ]);

  const suggestMobileNumber = useCallback(() => {
    suggestMobile()
      .then((res) => {
        const authMapSteps = getAuthSteps();
        deleteAuthSteps();
        if (res?.isEligible) {
          let action = trackingEvent.CTA_SUGGESTED_EMAIL;
          if (authMapSteps) {
            if (authMapSteps == LOGIN_CODES.EMAIL_PASSWORD) {
              action = trackingEvent.CTA_EMAIL_PASS_SOCIAL;
            }
            if (authMapSteps === LOGIN_CODES.GOOGLE_ONE_TAP) {
              action = trackingEvent.CTA_GOOGLE_ONE_TAP;
            }
            if (authMapSteps === LOGIN_CODES.EMAIL_OTP) {
              action = trackingEvent.CTA_EMAIL_OTP;
            } else if (authMapSteps > 1) {
              action = trackingEvent.CTA_SOCIAL;
            }
          }
          pushMobileVerificationEvents({ actionName: action, data: res });
          const encodedUrl = encodeURIComponent(
            `${window.location.pathname}${window.location.search}`
          );
          const url = `${MOBILE_VERIFICATION_URL}?redirect=${encodedUrl}`;
          const redirectUrl = checkValidHost(url, domains);
          setTimeout(() => {
            setShowIframe(true);
            setIframeUrl(redirectUrl);
          }, 500);
        }
      })
      .catch(() => {
        deleteAuthSteps();
      });
  }, [suggestMobile, domains]);

  useEffect(() => {
    if (dataFetched && configFlagsFetched) {
      handleAutoSignIn();
    }
  }, [configFlagsFetched, dataFetched, handleAutoSignIn]);

  useEffect(() => {
    if (!dataFetched || window.location?.pathname?.indexOf(AUTH_URL) === 0) {
      setShowIframe(false);
      return;
    }
    pushData({ cartCount: getCartCountFromStorage() });
    const authMapSteps = getAuthSteps();
    // 1 suggested email flow
    // 2 login source google
    // 3 facebook
    if (authMapSteps === LOGIN_CODES.GOOGLE) {
      pushData({ googleSuccess: true });
    }
    if (
      isSuggestedMobileMap &&
      dataFetched &&
      authMapSteps &&
      email &&
      !window.location.href.includes(GOOGLE_ONE_EXPERIEMNT) &&
      window.location?.pathname?.indexOf(MOBILE_VERIFICATION_URL) < 0
    ) {
      suggestMobileNumber();
    } else {
      deleteAuthSteps();
    }
  }, [dataFetched, email, suggestMobileNumber, isSuggestedMobileMap]);

  useEffect(() => {
    dispatch({ type: appEvents.SET_PATHNAME });
  });

  useEffect(() => {
    if (isPricesRevealedActive && bucketId) {
      pushData({ bucketId });
    }
  }, [bucketId, isPricesRevealedActive]);

  if (showIframe && dataFetched) {
    return (
      <FrameWrapper id="mobmapping_frame">
        <Iframe
          src={iframeUrl}
          allowFullScreen
        />
      </FrameWrapper>
    );
  }

  return (
    <>
      <WishList
        dataFetched={dataFetched}
        isLoggedIn={!!email}
      />
    </>
  );
};

export default Boot;
